import React, { useEffect, useMemo, useState } from "react";
import styles from "./studio.module.scss";
import _ from "lodash";
import cx from 'classnames';

import { Simulation } from "./card";
import { ScrollableContainer } from "src/components/scroller";
import { useDispatch } from "react-redux";
import { fetchDraftCourses, fetchPublishedCourses } from "src/redux/actions/coaching/studio";
import InfiniteScroll from "src/components/infiniteScroll";
import { useSelector } from "react-redux";
import { DraftCourseSummary } from "src/types/coaching/courses";
import CreateSimulationDialog from "../manage/simulations/components/simulationTypeDialog";
import { createSimulation, invalidateSimulationCreationState } from "src/redux/actions/coaching/simulation";
import { useNavigate } from "react-router-dom";

export const StudioPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showCreateSimulationDialog, setShowCreateSimulationDialog] = React.useState(false);

  const { creatingSimulation, createdSimulation } = useSelector((state) => {
    const creatingSimulation = _.get(state, 'coaching.simulations.creation.creating');
    const createdSimulation = _.get(state, 'coaching.simulations.creation.item');

    return {
      creatingSimulation,
      createdSimulation
    };
  }, _.isEqual);

  const { loading } = useSelector((state) => {
    const coursesState = _.get(state, `coaching.studio.drafts`);
    const loading = _.get(coursesState, 'loading') || false;

    return {
      loading
    };
  }, _.isEqual);

  const featuredCourse = useSelector((state) => {
    const featured = _.get(state, `coaching.studio.featured`) || null;
    return featured as DraftCourseSummary | null;
  });

  const handleCreateSimulation = (type: string) => {
    dispatch(createSimulation({
      name: 'Untitled Simulation',
      type: type
    }));
  }

  useEffect(() => {
    if (createdSimulation) {
      const id = _.get(createdSimulation, 'id');
      dispatch(invalidateSimulationCreationState());
      navigate(`/coaching/manage/simulations/draft/${id}`);

      setShowCreateSimulationDialog(false);
    }
  }, [createdSimulation]);

  return (
    <div className={styles.container}>
      <div className={styles.creationRow}>
        <div className={styles.recentlyEdited}>
          {loading && !featuredCourse && (
            <>
              <h2>Experience that you created recently...</h2>
              <div className={styles.entity}>
                <Simulation.PlaceholderFeatured />
              </div>
            </>
          )}
          {featuredCourse && (
            <>
              <h2>Experience that you created recently...</h2>
              <div className={styles.entity}>
                <Simulation.Featured simulation={featuredCourse?.course} url={`/coaching/manage/simulations/draft/${featuredCourse?.course?.id}`} />
              </div>
            </>
          )}
        </div>
        <div className={styles.actions}>
          {/* <button className={cx(styles.create, { [styles.creating]: false })}
            disabled={creatingSimulation}
            onClick={() => setShowCreateSimulationDialog(true)}
          >
            <img src="/images/coaching/create_podcast.svg" width={110} height={110} alt="Create Podcast" />
            <i className={cx(styles.icon, 'paperflite-mic')} />
            <div className={styles.name}>create a podcast</div>
            <p className={styles.description}>choose your deep dive conversation based on PDFs, websites, text and many more...</p>
          </button> */}
          <button className={cx(styles.create, { [styles.creating]: creatingSimulation })}
            disabled={creatingSimulation}
            onClick={() => setShowCreateSimulationDialog(true)}
          >
            <img src="/images/coaching/create_simulation.svg" width={110} height={110} alt="Create Simulation" />
            <i className={cx(styles.icon, 'paperflite-phone-call')} />
            <div className={styles.name}>{creatingSimulation ? 'creating call simulation...' : 'create call simulation'}</div>
            <p className={styles.description}>Create realistic call scenarios for your reps to practice. Perfect for dry runs that build skills and confidence—no pressure, just prep and more wins</p>
          </button>
        </div>
      </div>
      <DraftCourses />
      <PublishedCourses />
      <CreateSimulationDialog
        show={showCreateSimulationDialog}
        handleClose={() => setShowCreateSimulationDialog(false)}
        handleSelect={handleCreateSimulation}
      />
    </div>
  );
}

const DraftCourses: React.FC<object> = () => {
  const dispatch = useDispatch();

  const [courseTypes, setCourseTypes] = useState<string[]>([]);

  const { courses, loading, hasMoreItems, nextPage, total } = useSelector((state) => {
    const coursesState = _.get(state, `coaching.studio.drafts`);

    const courses = _.get(coursesState, 'items') || [] as DraftCourseSummary[];
    const loading = _.get(coursesState, 'loading') || false;
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems") || false;

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      courses,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);

  const fetchCoursesForPage = (page: number): void => {
    dispatch(fetchDraftCourses({ courseTypes: ['simulation'], page, limit: 5 }));
  };

  const handleLoadMoreCourses = () => {
    if (nextPage !== null) fetchCoursesForPage(nextPage);
  };

  useEffect(() => {
    fetchCoursesForPage(0);
  }, []);

  const toggleCourseType = (type: string) => {
    if (courseTypes.includes(type)) {
      setCourseTypes(courseTypes.filter((t) => t !== type));
    } else {
      setCourseTypes([...courseTypes, type]);
    }
  };

  if (!loading && _.size(courses) === 0) {
    return null;
  }

  return (
    <div className={styles.draftsRow}>
      <header className={styles.draftsHeader}>
        <h2>continue from where you stopped creating</h2>
        {/* <div className={styles.actions}>
          <Button variant="gradient"
            className={cx(styles.action, {
              [styles.active]: courseTypes.includes('podcast')
            })}
            icon={courseTypes.includes('podcast') ? "paperflite-tick" : undefined}
            text="Podcast"
            onClick={() => toggleCourseType('podcast')}
          />
          <Button variant="gradient"
            className={cx(styles.action, {
              [styles.active]: courseTypes.includes('simulation')
            })}
            icon={courseTypes.includes('simulation') ? "paperflite-tick" : undefined}
            text="Simulation"
            onClick={() => toggleCourseType('simulation')}
          />
        </div> */}
      </header>
      <ScrollableContainer distance={300} wrapperClassName={styles.draftsList}>
        <InfiniteScroll
          className={styles.courseList}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMoreCourses}
          loader={() => {
            return _.times(_.size(courses) == 0 ? 3 : 1).map((i) => (
              <Simulation.PlaceholderCard key={i} />
            ));
          }}
        >
          {courses.map((course, index) => {
            if (course?.entityType === "simulation") {
              const url = `/coaching/manage/simulations/draft/${course?.course?.id}`;
              return <Simulation.Card key={course.id || index} simulation={course?.course} url={url} status="draft" />
            }

            return false;
          })}
        </InfiniteScroll>
      </ScrollableContainer>
    </div>
  );
}

const PublishedCourses: React.FC<object> = () => {
  const dispatch = useDispatch();

  const { courses, loading, hasMoreItems, nextPage, total } = useSelector((state) => {
    const coursesState = _.get(state, `coaching.studio.published`);

    const courses = _.get(coursesState, 'items') || [] as DraftCourseSummary[];
    const loading = _.get(coursesState, 'loading') || false;
    const pagination = _.get(coursesState, 'pagination');
    const total = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems") || false;

    let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage && nextPage >= total) {
      nextPage = null;
    }

    return {
      courses,
      loading,
      hasMoreItems,
      nextPage,
      total
    };
  }, _.isEqual);

  const fetchCoursesForPage = (page: number): void => {
    dispatch(fetchPublishedCourses({
      courseTypes: ['simulation'],
      page,
      limit: 15
    }));
  };

  const handleLoadMoreCourses = () => {
    if (nextPage !== null) fetchCoursesForPage(nextPage);
  };

  useEffect(() => {
    fetchCoursesForPage(0);
  }, []);

  if (!loading && _.size(courses) === 0) {
    return null;
  }

  return (
    <div className={styles.publishedRow}>
      <header className={styles.publishedHeader}>
        <h2>experiences you have created</h2>
      </header>
      <ScrollableContainer distance={300} wrapperClassName={styles.publishedList}>
        <InfiniteScroll
          className={styles.courseList}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={handleLoadMoreCourses}
          loader={() => {
            return _.times(_.size(courses) == 0 ? 3 : 1).map((i) => (
              <Simulation.PlaceholderCard key={i} />
            ));
          }}
        >
          {courses.map((course, index) => {
            if (course?.entityType === "simulation") {
              const url = `/coaching/manage/simulations/draft/${course?.course?.id}`;
              return <Simulation.Card key={course.id || index} simulation={course?.course} url={url} status="published" />
            }

            return false;
          })}
        </InfiniteScroll>
      </ScrollableContainer>
    </div>
  );
}